import { useState, useMemo } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { domainPrices, getNormalizedPrice, type DomainPrice } from "@/data/domain-prices";
import { Header } from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowUpDown, Search, ExternalLink, RefreshCw, Globe, DollarSign, ArrowRightLeft, Calendar, TrendingUp, Languages, Bell, Sparkles } from "lucide-react";
import { Link } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export default function Home() {
  // The userAuth hooks provides authentication state
  // To implement login/logout functionality, simply call logout() or redirect to getLoginUrl()
  let { user, loading, error, isAuthenticated, logout } = useAuth();

  const { t, language, setLanguage } = useLanguage();
  const [search, setSearch] = useState("");
  const [batchSearch, setBatchSearch] = useState("");
  const [mode, setMode] = useState<'single' | 'batch'>('single');
  const [currency, setCurrency] = useState<'CNY' | 'USD'>('CNY');
  const [sortConfig, setSortConfig] = useState<{ key: keyof DomainPrice | 'valueScore'; direction: 'asc' | 'desc' }>({ key: 'registerPrice', direction: 'asc' });
  const [selectedTld, setSelectedTld] = useState<string>("all");

  const uniqueTlds = useMemo(() => Array.from(new Set(domainPrices.map(d => d.tld))).sort(), []);

  const filteredData = useMemo(() => {
    let data = domainPrices;

    if (mode === 'batch') {
      const domains = batchSearch.split(/[\n,]+/).map(d => d.trim().toLowerCase()).filter(Boolean);
      if (domains.length > 0) {
        data = data.filter(item => {
          // Check if item matches any of the input domains (e.g. input "example.com" matches tld ".com")
          return domains.some(d => d.endsWith(item.tld));
        });
      }
    } else {
      const q = search.trim().toLowerCase();
      // 允许用户直接输入完整域名，比如 "example.com" / "foo.bar.com"
      // 这种情况下：优先按最后一个 ".tld" 匹配
      let tldFromDomain: string | null = null;
      if (q.includes(".")) {
        const idx = q.lastIndexOf(".");
        const maybeTld = q.slice(idx); // 包含点，例如 ".com"
        if (uniqueTlds.includes(maybeTld)) tldFromDomain = maybeTld;
      } else if (q.startsWith(".")) {
        // 输入 ".com" 这种
        if (uniqueTlds.includes(q)) tldFromDomain = q;
      }

      data = data.filter((item) => {
        const tldOk =
          tldFromDomain ? item.tld === tldFromDomain : (selectedTld === "all" || item.tld === selectedTld);
        if (!tldOk) return false;

        if (!q) return true;
        return (
          item.registrar.toLowerCase().includes(q) ||
          item.tld.toLowerCase().includes(q) ||
          (tldFromDomain ? item.tld === tldFromDomain : false)
        );
      });
    }

    return data.sort((a, b) => {
      let aValue: number, bValue: number;

      if (sortConfig.key === 'valueScore') {
        aValue = a.valueScore ?? 0;
        bValue = b.valueScore ?? 0;
      } else if (sortConfig.key === 'registerPrice' || sortConfig.key === 'renewPrice' || sortConfig.key === 'transferPrice') {
        aValue = getNormalizedPrice(a[sortConfig.key] as number, a.currency);
        bValue = getNormalizedPrice(b[sortConfig.key] as number, b.currency);
      } else {
        // Fallback for other keys if any
        aValue = 0;
        bValue = 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [search, batchSearch, mode, selectedTld, sortConfig, currency]);

  const handleSort = (key: keyof DomainPrice | 'valueScore') => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const formatPrice = (price: number, itemCurrency: 'CNY' | 'USD') => {
    if (currency === itemCurrency) {
      return itemCurrency === 'USD' ? `$${price.toFixed(2)}` : `¥${price.toFixed(2)}`;
    }
    const converted = itemCurrency === 'USD' 
      ? price * 7.25 
      : price / 7.25;
    return currency === 'USD' ? `$${converted.toFixed(2)}` : `¥${converted.toFixed(2)}`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-sans selection:bg-primary/20">
      <Header />
      {/* Hero Section */}
      <div className="relative overflow-hidden border-b border-border/40 bg-card/30 backdrop-blur-sm">
        <div className="absolute inset-0 bg-grid-white/5 [mask-image:linear-gradient(0deg,transparent,black)]" />
        <div className="container relative py-16 md:py-24">
          <div className="flex flex-col items-center text-center space-y-6 max-w-3xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-center space-x-3 mb-4"
            >
              <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center border border-primary/30 shadow-[0_0_15px_rgba(var(--primary),0.3)]">
                <Globe className="h-6 w-6 text-primary" />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-white/60">
                {t.title}
              </h1>
            </motion.div>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-lg md:text-xl text-muted-foreground max-w-2xl"
            >
              {t.subtitle}
            </motion.p>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-col w-full max-w-2xl mt-8 gap-4"
            >
              <div className="flex justify-center gap-4 mb-2">
                <Button 
                  variant={mode === 'single' ? 'default' : 'outline'} 
                  onClick={() => setMode('single')}
                  className="w-32"
                >
                  {t.single}
                </Button>
                <Button 
                  variant={mode === 'batch' ? 'default' : 'outline'} 
                  onClick={() => setMode('batch')}
                  className="w-32"
                >
                  {t.batch}
                </Button>
              </div>

              {mode === 'single' ? (
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center w-full">
                  <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input 
                      placeholder={t.searchPlaceholder}
                      className="pl-10 bg-background/50 border-primary/20 focus:border-primary/50 h-12"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <Select value={selectedTld} onValueChange={setSelectedTld}>
                    <SelectTrigger className="w-full sm:w-[140px] h-12 bg-background/50 border-primary/20">
                      <SelectValue placeholder={t.allTlds} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t.allTlds}</SelectItem>
                      {uniqueTlds.map(tld => (
                        <SelectItem key={tld} value={tld}>{tld}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <Textarea 
                  placeholder={t.batchSearchPlaceholder}
                  className="bg-background/50 border-primary/20 focus:border-primary/50 min-h-[100px]"
                  value={batchSearch}
                  onChange={(e) => setBatchSearch(e.target.value)}
                />
              )}

              <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mt-2 w-full max-w-md sm:max-w-none mx-auto">
                {isAuthenticated ? (
                  <>
                    <Link href="/monitor" className="w-full sm:w-auto">
                      <Button variant="outline" className="h-10 w-full sm:w-auto">
                        <Bell className="h-4 w-4 mr-2" />
                        {t.monitor.title}
                      </Button>
                    </Link>
                    <Link href="/generator" className="w-full sm:w-auto">
                      <Button variant="outline" className="h-10 w-full sm:w-auto">
                        <Sparkles className="h-4 w-4 mr-2" />
                        {t.generator.title}
                      </Button>
                    </Link>
                  </>
                ) : (
                  <Link href="/login" className="w-full sm:w-auto">
                    <Button variant="default" className="h-10 w-full sm:w-auto">
                      {language === 'zh' ? '登录' : 'Login'}
                    </Button>
                  </Link>
                )}
                <div className="flex items-center bg-background/50 rounded-md border border-primary/20 p-1 h-10 w-full sm:w-auto justify-center">
                  <Button 
                    variant={currency === 'USD' ? 'default' : 'ghost'} 
                    size="sm" 
                    onClick={() => setCurrency('USD')}
                    className="h-full px-4 rounded-sm"
                  >
                    USD
                  </Button>
                  <Button 
                    variant={currency === 'CNY' ? 'default' : 'ghost'} 
                    size="sm" 
                    onClick={() => setCurrency('CNY')}
                    className="h-full px-4 rounded-sm"
                  >
                    CNY
                  </Button>
                </div>
                <div className="flex items-center bg-background/50 rounded-md border border-primary/20 p-1 h-10 w-full sm:w-auto justify-center">
                  <Button 
                    variant={language === 'en' ? 'default' : 'ghost'} 
                    size="sm" 
                    onClick={() => setLanguage('en')}
                    className="h-full px-4 rounded-sm"
                  >
                    EN
                  </Button>
                  <Button 
                    variant={language === 'zh' ? 'default' : 'ghost'} 
                    size="sm" 
                    onClick={() => setLanguage('zh')}
                    className="h-full px-4 rounded-sm"
                  >
                    中文
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="container py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="glass border-primary/10">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t.stats.registration}</CardTitle>
              <DollarSign className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono">
                {formatPrice(Math.min(...domainPrices.filter(d => d.tld === '.com').map(d => getNormalizedPrice(d.registerPrice, d.currency))), currency)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {t.stats.foundAt} {domainPrices.filter(d => d.tld === '.com').sort((a, b) => getNormalizedPrice(a.registerPrice, a.currency) - getNormalizedPrice(b.registerPrice, b.currency))[0]?.registrar}
              </p>
            </CardContent>
          </Card>
          <Card className="glass border-primary/10">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t.stats.renewal}</CardTitle>
              <RefreshCw className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono">
                {formatPrice(Math.min(...domainPrices.filter(d => d.tld === '.com').map(d => getNormalizedPrice(d.renewPrice, d.currency))), currency)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {t.stats.foundAt} {domainPrices.filter(d => d.tld === '.com').sort((a, b) => getNormalizedPrice(a.renewPrice, a.currency) - getNormalizedPrice(b.renewPrice, b.currency))[0]?.registrar}
              </p>
            </CardContent>
          </Card>
          <Card className="glass border-primary/10">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t.stats.transfer}</CardTitle>
              <ArrowRightLeft className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono">
                {formatPrice(Math.min(...domainPrices.filter(d => d.tld === '.com').map(d => getNormalizedPrice(d.transferPrice, d.currency))), currency)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {t.stats.foundAt} {domainPrices.filter(d => d.tld === '.com').sort((a, b) => getNormalizedPrice(a.transferPrice, a.currency) - getNormalizedPrice(b.transferPrice, b.currency))[0]?.registrar}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Table */}
        <Card className="glass border-primary/10 overflow-hidden">
          <CardHeader>
            <CardTitle>{t.title}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-primary/10">
                    <TableHead className="w-[100px]">{t.table.tld}</TableHead>
                    <TableHead className="w-[200px]">{t.table.registrar}</TableHead>
                    <TableHead className="cursor-pointer hover:text-primary transition-colors" onClick={() => handleSort('registerPrice')}>
                      <div className="flex items-center">
                        {t.table.registration}
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:text-primary transition-colors" onClick={() => handleSort('renewPrice')}>
                      <div className="flex items-center">
                        {t.table.renewal}
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:text-primary transition-colors" onClick={() => handleSort('transferPrice')}>
                      <div className="flex items-center">
                        {t.table.transfer}
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:text-primary transition-colors" onClick={() => handleSort('valueScore')}>
                      <div className="flex items-center">
                        {t.table.value}
                        <TrendingUp className="ml-2 h-4 w-4" />
                      </div>
                    </TableHead>
                    <TableHead>{t.table.tags}</TableHead>
                    <TableHead className="text-right">{t.table.action}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <AnimatePresence>
                    {filteredData.map((item) => (
                      <motion.tr
                        key={item.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        layout
                        className="group hover:bg-primary/5 border-primary/5 transition-colors"
                      >
                        <TableCell className="font-medium text-lg">{item.tld}</TableCell>
                        <TableCell className="font-medium">{item.registrar}</TableCell>
                        <TableCell className="font-mono text-primary font-bold">
                          {formatPrice(item.registerPrice, item.currency)}
                        </TableCell>
                        <TableCell className="font-mono text-muted-foreground">
                          {formatPrice(item.renewPrice, item.currency)}
                        </TableCell>
                        <TableCell className="font-mono text-muted-foreground">
                          {formatPrice(item.transferPrice, item.currency)}
                        </TableCell>
                        <TableCell>
                          {item.valueScore && (
                            <div className="flex items-center gap-2">
                              <div className="h-2 w-16 bg-secondary rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-primary" 
                                  style={{ width: `${item.valueScore}%` }}
                                />
                              </div>
                              <span className="text-xs font-mono">{item.valueScore}</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {item.tags?.map(tag => (
                              <Badge key={tag} variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20 border-primary/20">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {item.priceHistory && (
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <Calendar className="h-4 w-4" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="glass border-primary/20">
                                  <DialogHeader>
                                    <DialogTitle>{t.history.title} - {item.registrar} {item.tld}</DialogTitle>
                                  </DialogHeader>
                                  <div className="h-[300px] w-full mt-4">
                                    <ResponsiveContainer width="100%" height="100%">
                                      <LineChart data={item.priceHistory}>
                                        <XAxis dataKey="date" stroke="var(--muted-foreground)" fontSize={12} />
                                        <YAxis stroke="var(--muted-foreground)" fontSize={12} />
                                        <Tooltip 
                                          contentStyle={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
                                          itemStyle={{ color: 'var(--foreground)' }}
                                        />
                                        <Line 
                                          type="monotone" 
                                          dataKey="price" 
                                          stroke="var(--primary)" 
                                          strokeWidth={2}
                                          dot={{ fill: 'var(--primary)' }}
                                        />
                                      </LineChart>
                                    </ResponsiveContainer>
                                  </div>
                                  <div className="flex justify-between text-sm text-muted-foreground mt-2">
                                    <span>{t.history.lowest}: {formatPrice(Math.min(...item.priceHistory.map(p => p.price)), item.currency)}</span>
                                    <span>{t.history.current}: {formatPrice(item.registerPrice, item.currency)}</span>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                              asChild
                            >
                              <a href={item.url} target="_blank" rel="noopener noreferrer">
                                {t.table.visit} <ExternalLink className="ml-2 h-4 w-4" />
                              </a>
                            </Button>
                          </div>
                        </TableCell>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
